import { IProductCatalogLookupBulkDto, IProductCatalogLookupDto } from "../../products/models/ProductCatalog";
import { getCatalogLookupsBulk, filterOutLookupsByParentId, getLeadtimeProductGroups, getAllProductCodesForLeadTimeGroup, filterOutProductCode } from "../../products/api/ProductCatalogApi";
import { useState, useEffect } from 'react';
import { usePlant } from "../../components/PlantHook";

type Props = {
  productCategory?: string;
  productCode?: string;
}

interface IPapPair {
  key: string;
  description: string;
  options: { option: string; values: string[] }[];
}

const ProductCatalogWidget = ({ productCategory, productCode }: Props) => {
  const [papPairs, setPapPairs] = useState<IPapPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const plant = usePlant();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard:', text);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const setProductCodes = (productCodes: IProductCatalogLookupDto[], hive: IProductCatalogLookupBulkDto[]) => {
    const data = productCodes.map((code: IProductCatalogLookupDto) => {
        const optionKeys = filterOutLookupsByParentId(hive, code.id);
        const options = optionKeys.map((optionKey: IProductCatalogLookupDto) => {
          const optionValues = filterOutLookupsByParentId(hive, optionKey.id);
          return {
            option: optionKey.key,
            values: optionValues.map((optionValue: IProductCatalogLookupDto) => optionValue.key)
          };
        });
        return { key: code.key, description: code.description, options: options };
      });
      setPapPairs(data);
  }

  useEffect(() => {
    setLoading(true);
    getLeadtimeProductGroups().then(leadTimeGroups => {
      getCatalogLookupsBulk()
        .then((hive: IProductCatalogLookupBulkDto[]) => {
            if (productCategory)    {
                getAllProductCodesForLeadTimeGroup(productCategory, leadTimeGroups, hive).then(productCodes => {
                    setProductCodes(productCodes, hive);
                    setLoading(false);
                });
            } else if (productCode) {
                const productCodes = filterOutProductCode(productCode, hive);
                setProductCodes(productCodes, hive);
                setLoading(false);
            }
        })
        .catch(error => {
          console.error('Failed to load catalog data:', error);
          setLoading(false);
        });
    });
  }, [plant, productCategory, productCode]);

  const filteredPapPairs = papPairs.filter((dto) => {
    const searchLower = searchText.toLowerCase();
    return (
      dto.key.toLowerCase().includes(searchLower) ||
      dto.description.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return <div className="text-center py-5">Loading...</div>;
  }

  return (
    <>
      {!productCode && <div className="mb-5">
        <div className="input-group search-input-group" style={{ maxWidth: '400px' }}>
          <input
            type='text'
            className='form-control'
            placeholder='Search by Article code or description'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <div className="input-group-append">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setSearchText('')}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
          )}
        </div>
      </div>}
      <div className="table-responsive">
        <table className="table table-head-custom table-vertical-center">
          <thead>
            <tr>
              <th className="pl-7" style={{ minWidth: '150px' }}>Article Code</th>
              <th style={{ minWidth: '200px' }}>Description</th>
              <th style={{ minWidth: '400px' }}>Options & Values</th>
            </tr>
          </thead>
          <tbody>
            {filteredPapPairs.map((dto, index) => (
              <tr key={index}>
                <td className="pl-7">
                  <span className="text-dark-75 font-weight-bolder d-block font-size-lg">
                    {dto.key}
                  </span>
                </td>
                <td>
                  <span className="text-muted font-weight-bold">
                    {dto.description}
                  </span>
                </td>
                <td>
                  {dto.options
                    .filter((option) => option.option !== 'Pages')
                    .map((option, optIndex) => (
                      <div key={optIndex} className="mb-3">
                        <span className="font-weight-bold text-primary">
                          {option.option}:
                          <i 
                            className="fa fa-copy ml-2 cursor-pointer"
                            onClick={() => copyToClipboard(option.option)}
                            title="Click to copy"
                            style={{ cursor: 'pointer' }}
                          ></i>
                        </span>
                        <div className="d-flex flex-wrap mt-1">
                          {option.values.filter(value => value !== '').map((value, valIndex) => (
                            <span
                              key={valIndex}
                              className="badge badge-light-info mr-2 mb-2"
                            >
                              {value}
                              <i 
                                className="fa fa-copy ml-1 cursor-pointer"
                                onClick={() => copyToClipboard(value)}
                                title="Click to copy"
                                style={{ cursor: 'pointer' }}
                              ></i>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export { ProductCatalogWidget };
